import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { LOGO_URL, SUPPORT_EMAIL } from "../constants/brand";
import { getHomePathForRole } from "../modules/registry";
import { SecurityBadge } from "../components/shared/SecurityBadge";
import { PageWatermark } from "../components/shared/PageWatermark";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  useEffect(() => {
    if (user) {
      navigate(getHomePathForRole(user.role), { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login({ identifier: identifier.trim(), password });
    setLoading(false);
    if (!res.success) {
      setError(res.error);
      return;
    }
    navigate(getHomePathForRole(res.user.role), { replace: true });
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotMsg(data.message + (data.dev_reset_link ? ` (Dev link: ${data.dev_reset_link})` : ""));
    } catch (e2) {
      setForgotMsg(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-auth-bg flex items-center justify-center p-6 relative overflow-hidden">
      <PageWatermark variant="auth" />
      <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #d9b44a 0, transparent 45%), radial-gradient(circle at 80% 80%, #d9b44a 0, transparent 45%)" }} />
      <div
        className="w-full max-w-md bg-auth-surface border border-auth-gold/25 p-10 relative z-10 auth-glow login-card-reveal"
        data-testid="login-card"
      >
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-28 h-28 object-contain mb-4 auth-glow" />
          <h1 className="font-serif text-2xl text-auth-cream tracking-tight text-center auth-glow-text">Iron Gate Private Portal</h1>
          <p className="text-auth-muted text-sm mt-2 tracking-wide">Private Access</p>
        </div>

        {!showForgot ? (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-auth-muted mb-2 block">ID or Email</label>
              <Input
                data-testid="login-identifier-input"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                title="Type the ID your admin gave you (like ABC001) or the email address you signed up with."
                className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12 focus-visible:ring-auth-gold"
                placeholder="e.g. ABC001 or you@example.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-auth-muted mb-2 block">Password</label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  title="Type your account password. After too many wrong attempts your account will be temporarily locked for security."
                  className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12 focus-visible:ring-auth-gold pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility-button"
                  onClick={() => setShowPassword(!showPassword)}
                  title="Click to reveal or hide the password you're typing, so you can double-check it before signing in."
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-auth-muted hover:text-auth-gold transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="text-auth-danger text-sm" data-testid="login-error">{error}</p>}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              title="Signs you in and takes you straight to your dashboard for your role (rep, manager, admin, or client)."
              className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black font-medium tracking-wide transition-all hover:-translate-y-0.5"
            >
              {loading ? "Signing in..." : "Enter Portal"}
            </Button>
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setShowForgot(true)}
              title="Click here if you can't remember your password — we'll email you a secure link to set a new one."
              className="w-full text-center text-sm text-auth-muted hover:text-auth-gold transition-colors mt-2"
            >
              Forgot Password?
            </button>
            <SecurityBadge variant="login" />
          </form>
        ) : (
          <form onSubmit={handleForgot} className="space-y-5" data-testid="forgot-password-form">
            <p className="text-auth-muted text-sm">Enter your registered email and we'll send you a reset link.</p>
            <Input
              data-testid="forgot-email-input"
              type="email"
              required
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              title="The email address on file for your account — we'll send the password reset link here."
              className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12"
              placeholder="you@example.com"
            />
            {forgotMsg && <p className="text-auth-gold text-xs" data-testid="forgot-password-message">{forgotMsg}</p>}
            <Button data-testid="forgot-submit-button" type="submit" title="Sends a password reset link to the email address above." className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black">
              Send Reset Link
            </Button>
            <button
              type="button"
              data-testid="back-to-login-link"
              onClick={() => setShowForgot(false)}
              title="Goes back to the normal login screen without resetting anything."
              className="w-full text-center text-sm text-auth-muted hover:text-auth-gold transition-colors"
            >
              Back to login
            </button>
          </form>
        )}

        <p className="text-center text-xs text-auth-muted mt-8">
          Need help? <span className="text-auth-gold">{SUPPORT_EMAIL}</span>
        </p>
      </div>
    </div>
  );
}
